import { useId, useMemo, useState, type FormEvent } from 'react';

import Button from '@/components/Button/Button';
import useDashboardWidgetMutations from '@/features/dashboard/hooks/useDashboardWidgetMutations';
import type { WidgetId } from '@/features/dashboard/registry';
import useCurrentHome from '@/features/homeAutomation/hooks/useCurrentHome';
import { cn } from '@/lib/cn';
import { errorInputClasses, inputClasses } from '@/lib/formStyles';

import {
  DEFAULT_SAUNA_WIDGET_CONFIG,
  HISTORY_DAYS_MAX,
  HISTORY_DAYS_MIN,
  POLL_SECONDS_MAX,
  POLL_SECONDS_MIN,
  mergeSaunaWidgetConfig,
  type SaunaChimeSound,
  type SaunaWidgetConfig,
} from './config';
import { playReadyChime } from './playReadyChime';

export default function SaunaSettingsForm({
  widgetId,
  config,
  onSaved,
  formId,
}: {
  widgetId: WidgetId;
  config: Record<string, unknown>;
  onSaved: () => void;
  formId: string;
}) {
  const mutations = useDashboardWidgetMutations();
  const initial = mergeSaunaWidgetConfig(config as Partial<SaunaWidgetConfig>);

  const homeQuery = useCurrentHome();
  const currentHomeId = homeQuery.data?.id ?? null;
  const homeOptions = useMemo(
    () => (currentHomeId ? [{ id: currentHomeId, name: homeQuery.data!.name }] : []),
    [currentHomeId, homeQuery.data],
  );

  const [homeId, setHomeId] = useState<string | null>(initial.homeId ?? currentHomeId);
  const [pollRaw, setPollRaw] = useState(String(initial.pollSeconds));
  const [daysRaw, setDaysRaw] = useState(String(initial.historyDays));
  const [showHistory, setShowHistory] = useState(initial.showHistory);
  const [soundOnReady, setSoundOnReady] = useState(initial.soundOnReady);
  const [chimeSound, setChimeSound] = useState<SaunaChimeSound>(initial.chimeSound);

  const effectiveHomeId = homeId ?? currentHomeId;
  const pollNum = Number(pollRaw);
  const daysNum = Number(daysRaw);
  const pollValid = Number.isFinite(pollNum) && Number.isInteger(pollNum) && pollNum >= POLL_SECONDS_MIN && pollNum <= POLL_SECONDS_MAX;
  const daysValid = Number.isFinite(daysNum) && Number.isInteger(daysNum) && daysNum >= HISTORY_DAYS_MIN && daysNum <= HISTORY_DAYS_MAX;
  const noHome = !effectiveHomeId;
  const canSave = !noHome && pollValid && daysValid && !mutations.isPending;

  const homeSelectId = useId();
  const pollId = useId();
  const pollErrId = useId();
  const daysId = useId();
  const daysErrId = useId();
  const histId = useId();
  const soundId = useId();
  const chimeId = useId();

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSave || !effectiveHomeId) return;
    const values: SaunaWidgetConfig = {
      homeId: effectiveHomeId,
      pollSeconds: pollNum,
      historyDays: daysNum,
      showHistory,
      soundOnReady,
      chimeSound,
    };
    mutations.updateConfig(widgetId, values as unknown as Record<string, unknown>);
    onSaved();
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
      {noHome ? (
        <div className="rounded-lg border border-dashed border-(--border-default) bg-(--bg-surface) px-4 py-6 text-sm text-(--text-secondary)">
          <p className="font-medium text-(--text-primary)">No home configured.</p>
          <p className="mt-1">
            Open <em>Home settings</em> from your user menu to create one first, then come back here.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-1.5">
            <label htmlFor={homeSelectId} className="text-xs font-medium tracking-wide text-(--text-secondary) uppercase">
              Home
            </label>
            <select
              id={homeSelectId}
              value={homeId ?? currentHomeId ?? ''}
              onChange={(e) => setHomeId(e.target.value || null)}
              className={inputClasses}
            >
              {homeOptions.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-(--text-tertiary)">Sauna sensor and thresholds are configured in Home settings (user menu).</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor={pollId} className="text-xs font-medium tracking-wide text-(--text-secondary) uppercase">
              Refresh interval (seconds)
            </label>
            <input
              id={pollId}
              type="number"
              min={POLL_SECONDS_MIN}
              max={POLL_SECONDS_MAX}
              step={1}
              required
              value={pollRaw}
              onChange={(e) => setPollRaw(e.target.value)}
              aria-invalid={!pollValid || undefined}
              aria-describedby={!pollValid ? pollErrId : undefined}
              className={cn(inputClasses, !pollValid && errorInputClasses)}
            />
            {!pollValid && (
              <p id={pollErrId} className="text-xs text-(--accent-danger)">
                Must be an integer between {POLL_SECONDS_MIN} and {POLL_SECONDS_MAX}.
              </p>
            )}
            <p className="text-xs text-(--text-tertiary)">
              Default {DEFAULT_SAUNA_WIDGET_CONFIG.pollSeconds}s. Polls faster automatically while warming/ready.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor={daysId} className="text-xs font-medium tracking-wide text-(--text-secondary) uppercase">
              History window (days)
            </label>
            <input
              id={daysId}
              type="number"
              min={HISTORY_DAYS_MIN}
              max={HISTORY_DAYS_MAX}
              step={1}
              required
              value={daysRaw}
              onChange={(e) => setDaysRaw(e.target.value)}
              aria-invalid={!daysValid || undefined}
              aria-describedby={!daysValid ? daysErrId : undefined}
              className={cn(inputClasses, !daysValid && errorInputClasses)}
            />
            {!daysValid && (
              <p id={daysErrId} className="text-xs text-(--accent-danger)">
                Must be an integer between {HISTORY_DAYS_MIN} and {HISTORY_DAYS_MAX}.
              </p>
            )}
          </div>

          <label htmlFor={histId} className="flex items-center gap-2 text-sm text-(--text-primary)">
            <input
              id={histId}
              type="checkbox"
              checked={showHistory}
              onChange={(e) => setShowHistory(e.target.checked)}
              className="h-4 w-4 rounded border-(--border-default) accent-(--accent-primary)"
            />
            Show sparkline in tile
          </label>

          <div className="flex flex-col gap-2">
            <label htmlFor={soundId} className="flex items-center gap-2 text-sm text-(--text-primary)">
              <input
                id={soundId}
                type="checkbox"
                checked={soundOnReady}
                onChange={(e) => setSoundOnReady(e.target.checked)}
                className="h-4 w-4 rounded border-(--border-default) accent-(--accent-primary)"
              />
              Play chime when sauna becomes ready
            </label>
            <label htmlFor={chimeId} className="flex flex-col gap-1.5">
              <span className="text-xs font-medium tracking-wide text-(--text-secondary) uppercase">Chime sound</span>
              <select
                id={chimeId}
                value={chimeSound}
                onChange={(e) => setChimeSound(e.target.value as SaunaChimeSound)}
                disabled={!soundOnReady}
                className={inputClasses}
              >
                <option value="sample">Recorded sauna sample</option>
                <option value="tone">Synthesised two-note bell</option>
              </select>
            </label>
            <div>
              <Button type="button" variant="ghost" size="sm" disabled={!soundOnReady} onClick={() => playReadyChime(chimeSound)}>
                Test sound
              </Button>
            </div>
          </div>
        </>
      )}
    </form>
  );
}
